// Metas (cofrinhos): viagem, reserva de emergência, carro... Valor-alvo, prazo, quanto guardar por mês e o progresso.
import { Recolhivel } from '../Recolhivel';
import { useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { Financas, Meta } from '../../store/types';
import { duplicarMeta, excluirMeta, metasDe, movimentarMeta, ordenarMetas, porMesMeta, saldoMeta, salvarMeta } from '../../store/financas';
import { Ordenavel } from '../Arrastar';
import { AcoesItem, useAcoes } from '../Acoes';
import { Arquivados, useSelecao } from '../Selecao';
import { useOrganizar } from '../../lib/organizar';
import { paraLixeira } from '../../lib/lixeira';
import { PickerModal } from '../Pickers';
import { ActionSheet, Btn, Chip, Field, Sheet, type Action } from '../ui';
import { C, alpha } from '../theme';
import { addDays, brYmd, parseYmd, today, uid, ymd } from '../../lib/date';
import { lerValor } from '../../lib/moeda';
import { t } from '../../lib/i18n';
import { useCaixas } from '../../lib/caixas';
import { ROLAGEM_TECLADO } from '../teclado';

const ICONES = ['🎯', '✈️', '🛟', '🚗', '🏠', '🎓', '💍', '👶', '🏖️', '💻', '🎁', '🐶'];
export type FormMeta = { id: string; nome: string; icone: string; alvoTxt: string; prazo: string | null; inicialTxt: string; criada: string; novo: boolean };
export const metaVazia = (): FormMeta => ({ id: uid(), nome: '', icone: '🎯', alvoTxt: '', prazo: null, inicialTxt: '', criada: today(), novo: true });

/** Exclui a meta guardando na lixeira (30 dias). */
export function apagarMeta(m: Meta) {
  paraLixeira('financas', 'lista', m.nome, m, { doc: 'financas', campo: 'metas' });
  excluirMeta(m.id);
}

export function SecaoMetas({ d, cor, $, novaMeta }: { d: Financas; cor: string; $: (v: number) => string; novaMeta: () => void }) {
  const metas = metasDe(d);
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const [form, setForm] = useState<FormMeta | null>(null);
  const [mov, setMov] = useState<{ m: Meta; retirar: boolean; txt: string } | null>(null);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const { confirmar } = useAcoes();
  const org = useOrganizar('financas-meta');
  const sep = org.separar(metas, m => m.id);
  const sel = useSelecao({ itens: sep.ativos, chave: m => m.id, cor, grande: () => true, onArquivar: ids => org.arquivar(ids),
    onExcluir: ids => ids.forEach(id => { const m = metas.find(x => x.id === id); if (m) apagarMeta(m); }) });
  const excluirConf = (m: Meta) => Alert.alert(t('Excluir a meta "{m}"?', { m: m.nome }), t('O que já foi guardado continua nos lançamentos como investimento.') + '\n\n' + t('Fica 30 dias na lixeira.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: async () => { if (await confirmar(t('Confirme para excluir'))) apagarMeta(m); } }]);
  function movimentar() {
    if (!mov) return;
    const v = lerValor(mov.txt);
    if (!(v > 0)) return Alert.alert(t('Informe o valor.'));
    if (mov.retirar && v > saldoMeta(d, mov.m)) return Alert.alert(t('Valor maior que o guardado nesta meta.'));
    movimentarMeta(mov.m, mov.retirar ? -v : v);
    const novo = saldoMeta(d, mov.m) + (mov.retirar ? -v : v);
    setMov(null);
    if (!mov.retirar && novo >= mov.m.alvo) Alert.alert('🎉 ' + t('Meta atingida!'), t('Você juntou {v} para "{m}".', { v: $(novo), m: mov.m.nome }));
  }
  return (<>
    <Recolhivel titulo={t('METAS')} visual="titulo" chave="fin:metas" resumo={String(sep.ativos.length)} gap={10} desligado={!metas.length}>
    {sel.barra}
    <Ordenavel itens={sep.ativos} chave={m => m.id} onOrdem={l => ordenarMetas([...l, ...sep.arquivados])} selecao={sel.prop} render={m => {
      const s = saldoMeta(d, m), pct = m.alvo ? s / m.alvo : 0, pm = porMesMeta(d, m);
      return (
        <AcoesItem lembrar={{ aba: 'financas', id: m.id, titulo: m.nome }} nome={m.nome} protegido aviso={t('O que já foi guardado continua nos lançamentos como investimento.')} onExcluir={() => apagarMeta(m)}
          onDuplicar={() => duplicarMeta(m)} onArquivar={() => org.arquivar(m.id)} fixar={{ on: org.fixado(m.id), alternar: () => org.alternarFixo(m.id) }} pacote={() => ({ titulo: m.nome, emoji: m.icone, data: m.prazo, origem: 'financas', texto: t('{s} de {a}', { s: $(s), a: $(m.alvo) }) })}>
        <Pressable onPress={() => setMenu({ t: `${m.icone} ${m.nome}`, a: [
          { label: '➕ ' + t('Guardar dinheiro'), onPress: () => setMov({ m, retirar: false, txt: pm ? String(pm).replace('.', ',') : '' }) },
          ...(s > 0 ? [{ label: '➖ ' + t('Retirar'), onPress: () => setMov({ m, retirar: true, txt: '' }) }] : []),
          { label: t('Editar'), onPress: () => setForm({ ...m, alvoTxt: String(m.alvo).replace('.', ','), inicialTxt: m.inicial ? String(m.inicial).replace('.', ',') : '', novo: false }) },
          { label: '📦 ' + t('Arquivar'), onPress: () => org.arquivar(m.id) },
          { label: t('Excluir'), destructive: true, onPress: () => setTimeout(() => excluirConf(m), 300) },
        ] })} style={{ backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12), gap: cx.gap(6), borderWidth: 1, borderColor: pct >= 1 ? alpha(C.ok, 0.6) : C.line }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Text style={{ fontSize: cx.icone(22) }}>{m.icone}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={1}>{m.nome}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{pct >= 1 ? '🎉 ' + t('Meta atingida') : pm ? t('Guarde {v} por mês até {d}', { v: $(pm), d: brYmd(m.prazo!) }) : m.prazo ? t('Prazo: {d}', { d: brYmd(m.prazo) }) : t('Sem prazo')}</Text>
            </View>
            <Text style={{ color: C.text, fontWeight: '800' }}>{Math.min(100, Math.round(pct * 100))}%</Text>
          </View>
          <View style={{ height: 8, borderRadius: 4, backgroundColor: C.card2, overflow: 'hidden' }}>
            <View style={{ width: `${Math.max(2, Math.min(100, pct * 100))}%`, height: 8, backgroundColor: pct >= 1 ? C.ok : '#60A5FA' }} />
          </View>
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('{s} de {a}', { s: $(s), a: $(m.alvo) })}</Text>
        </Pressable>
        </AcoesItem>
      );
    }} />
    </Recolhivel>
    <Arquivados itens={sep.arquivados} chave={m => m.id} cor={cor} qualificador={t('Metas')} render={m => <Text style={{ color: C.text }}>{m.icone} {m.nome}</Text>}
      onRestaurar={m => org.restaurar(m.id)} onExcluir={excluirConf} />
    {metas.length > 0 && <Btn outline cor={cor} label={'🎯 ' + t('Nova meta')} onPress={novaMeta} />}

    <MetaFormSheet form={form} setForm={setForm} cor={cor} />
    <Sheet visible={!!mov} onClose={() => setMov(null)} title={mov?.retirar ? t('Retirar da meta') : t('Guardar na meta')}>
      {mov && (
        <View style={{ padding: 16, gap: 12 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>{mov.m.icone} {mov.m.nome}</Text>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t('Guardado até agora: {v}', { v: $(saldoMeta(d, mov.m)) })}</Text>
          <Field value={mov.txt} onChangeText={v => setMov({ ...mov, txt: v })} keyboardType="decimal-pad" placeholder={t('Valor')} autoFocus />
          <Btn cor={cor} label={mov.retirar ? t('Retirar') : t('Guardar')} onPress={movimentar} />
        </View>
      )}
    </Sheet>
    <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
  </>);
}

/** Cadastro ou edição de uma meta. */
export function MetaFormSheet({ form, setForm, cor }: { form: FormMeta | null; setForm: (f: FormMeta | null) => void; cor: string }) {
  const [picker, setPicker] = useState(false);
  function salvar() {
    if (!form) return;
    const alvo = lerValor(form.alvoTxt);
    if (!form.nome.trim()) return Alert.alert(t('Dê um nome à meta.'));
    if (!(alvo > 0)) return Alert.alert(t('Informe quanto quer juntar.'));
    salvarMeta({ id: form.id, nome: form.nome.trim(), icone: form.icone, alvo: Math.round(alvo * 100) / 100, prazo: form.prazo, inicial: Math.max(0, lerValor(form.inicialTxt) || 0), criada: form.criada });
    setForm(null);
  }
  return (
    <Sheet visible={!!form} onClose={() => setForm(null)} title={form?.novo ? t('Nova meta') : t('Editar meta')}
      right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      {form && (
        <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
          <Field value={form.nome} onChangeText={v => setForm({ ...form, nome: v })} placeholder={t('Nome (ex.: Viagem de férias, Reserva de emergência)')} />
          <Field value={form.alvoTxt} onChangeText={v => setForm({ ...form, alvoTxt: v })} keyboardType="decimal-pad" placeholder={t('Quanto quer juntar')} />
          <Field value={form.inicialTxt} onChangeText={v => setForm({ ...form, inicialTxt: v })} keyboardType="decimal-pad" placeholder={t('Quanto já tem guardado (opcional)')} />
          <Text style={{ color: C.sub }}>{t('Prazo')}</Text>
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            <Chip cor={cor} on={!!form.prazo} label={form.prazo ? `📅 ${brYmd(form.prazo)}` : '📅 ' + t('Escolher data')} onPress={() => setPicker(true)} />
            {!!form.prazo && <Chip cor={cor} on={false} small label={t('Sem prazo')} onPress={() => setForm({ ...form, prazo: null })} />}
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {ICONES.map(i => <Pressable key={i} onPress={() => setForm({ ...form, icone: i })} style={{ width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center',
              backgroundColor: form.icone === i ? alpha(cor, 0.3) : C.card2 }}><Text style={{ fontSize: 20 }}>{i}</Text></Pressable>)}
          </View>
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('Com prazo, o app calcula quanto guardar por mês. O dinheiro guardado conta como "Guardou" no mês.')}</Text>
          <Btn cor={cor} label={t('Salvar')} onPress={salvar} />
        </ScrollView>
      )}
      {picker && form && <PickerModal mode="date" cor={cor} value={parseYmd(form.prazo ?? addDays(today(), 365))} onClose={() => setPicker(false)} onPick={x => setForm({ ...form, prazo: ymd(x) })} />}
    </Sheet>
  );
}
